import { useMemo, useCallback, useState } from 'react';

import {
  BidOfferItem,
  BestBidOffer,
} from 'global/Securities/types/api/orderbook';
import SkeletonWrapper from 'global/Securities/components/SkeletonWrapper';
import If from 'global/ErrorHandlers/If';
import useBestBidOfferData from 'features/company/Stream/TableOrderbook/hooks/useBestBidData';
import TableOHLCSkeleton from './TableOHLCSkeleton';
import { bidOfferRowQuantity } from './constants';
import OHLCBestBidOffer from './TableOHLCBestBidOffer';
import {
  getTotalFrequencyVolume,
  getBidOfferList,
  formatCellValue,
  formatNumber,
  getTextColor,
  getLengthOrderbook,
} from './utils';
import {
  OHLCCellText,
  OHLCContainer,
  OHLCTable,
  OHLCTableBodyData,
  OHLCTableFoot,
  OHLCTableFootData,
  OHLCTableHead,
  OHLCTableHeadData,
} from './TableOHLC.styled';

interface TableOHLCProps {
  loading?: boolean;
  bid?: BidOfferItem;
  offer?: BidOfferItem;
  previous?: number;
  isEmpty?: boolean;
  bestBidOffer?: BestBidOffer;
  marketStatus?: string;
}

const TableOHLC = ({
  bid,
  offer,
  previous,
  loading,
  isEmpty = false,
  bestBidOffer,
  marketStatus = 'close',
}: TableOHLCProps) => {
  const lengthOrderbook = useMemo(() => {
    if (bid && offer) {
      return getLengthOrderbook(bid, offer);
    }

    return bidOfferRowQuantity;
  }, [bid, offer]);

  const bidList =
    useMemo(() => getBidOfferList(bid, lengthOrderbook), [bid, lengthOrderbook]);
  const offerList =
    useMemo(() => getBidOfferList(offer, lengthOrderbook), [offer, lengthOrderbook]);

  const { bid: bidTotal, offer: offerTotal } = useMemo(
    () =>
      getTotalFrequencyVolume(
        {
          frequency: bidList.frequency,
          volume: bidList.volume,
        },
        {
          frequency: offerList.frequency,
          volume: offerList.volume,
        },
        lengthOrderbook,
      ),
    [
      bidList.frequency.join(),
      bidList.volume.join(),
      offerList.frequency.join(),
      offerList.volume.join(),
      lengthOrderbook,
    ],
  );

  const initialBestBidOffer = useMemo(() => bestBidOffer, []);

  const { isBlured, showBestBid } = useBestBidOfferData(initialBestBidOffer, null, marketStatus);

  const getRowClassName = useCallback(() => {
    if (isBlured) {
      return 'blurred';
    }
    return '';
  }, [isBlured]);

  return (
    <SkeletonWrapper skeleton={<TableOHLCSkeleton />} loading={loading}>
      <OHLCContainer data-cy="ohlc-container">
        <OHLCTable>
          <If condition={!isEmpty || showBestBid}>
            <OHLCTableHead>
              <tr>
                <OHLCTableHeadData>Freq</OHLCTableHeadData>
                <OHLCTableHeadData>Lot</OHLCTableHeadData>
                <OHLCTableHeadData>Bid</OHLCTableHeadData>
                <OHLCTableHeadData>Offer</OHLCTableHeadData>
                <OHLCTableHeadData>Lot</OHLCTableHeadData>
                <OHLCTableHeadData>Freq</OHLCTableHeadData>
              </tr>
            </OHLCTableHead>
          </If>

          <tbody>
            <If condition={showBestBid}>
              <OHLCBestBidOffer data={bestBidOffer} previous={previous} />
            </If>

            <If condition={!isEmpty}>
              {new Array(lengthOrderbook).fill('').map((_, i) => (
                <tr
                  data-cy={`ohlc-row-${i}`}
                  key={`rows-${i.toString()}`}
                  className={getRowClassName()}
                >
                  <OHLCTableBodyData>
                    <OHLCCellText color="#8277F8" minWidth={40} maxWidth={55}>
                      {formatCellValue(bidList.frequency[i])}
                    </OHLCCellText>
                  </OHLCTableBodyData>

                  <OHLCTableBodyData>
                    <OHLCCellText minWidth={50}>
                      {formatCellValue(bidList.volume[i])}
                    </OHLCCellText>
                  </OHLCTableBodyData>

                  <OHLCTableBodyData>
                    <OHLCCellText
                      data-cy="bid-price"
                      minWidth={50}
                      maxWidth={55}
                      color={getTextColor(bidList.price[i], previous)}
                    >
                      {formatCellValue(bidList.price[i])}
                    </OHLCCellText>
                  </OHLCTableBodyData>

                  <OHLCTableBodyData>
                    <OHLCCellText
                      data-cy="offer-price"
                      minWidth={50}
                      maxWidth={55}
                      color={getTextColor(offerList.price[i], previous)}
                    >
                      {formatCellValue(offerList.price[i])}
                    </OHLCCellText>
                  </OHLCTableBodyData>

                  <OHLCTableBodyData>
                    <OHLCCellText minWidth={50}>
                      {formatCellValue(offerList.volume[i])}
                    </OHLCCellText>
                  </OHLCTableBodyData>

                  <OHLCTableBodyData>
                    <OHLCCellText minWidth={40} maxWidth={55} color="#8277F8">
                      {formatCellValue(offerList.frequency[i])}
                    </OHLCCellText>
                  </OHLCTableBodyData>
                </tr>
              ))}
            </If>

            <If condition={isEmpty && showBestBid}>
              {Array.from({ length: 10 }).map((_, idx) => (
                <tr key={`empty-row-${idx.toString()}`}>
                  {Array.from({ length: 6 }).map((__, idy) => (
                    <OHLCTableBodyData key={`empty-${idy.toString()}`}>
                      <OHLCCellText minWidth={40} maxWidth={55}>
                        -
                      </OHLCCellText>
                    </OHLCTableBodyData>
                  ))}
                </tr>
              ))}
            </If>
          </tbody>

          <If condition={!isEmpty}>
            <OHLCTableFoot>
              <tr className={getRowClassName()}>
                <OHLCTableFootData data-cy="total-bid-frequency">
                  {formatNumber(bidTotal.frequency)}
                </OHLCTableFootData>
                <OHLCTableFootData data-cy="total-bid-volume">
                  {formatNumber(bidTotal.volume)}
                </OHLCTableFootData>
                <OHLCTableFootData colSpan={2}>TOTAL</OHLCTableFootData>
                <OHLCTableFootData data-cy="total-offer-volume">
                  {formatNumber(offerTotal.volume)}
                </OHLCTableFootData>
                <OHLCTableFootData data-cy="total-offer-frequency">
                  {formatNumber(offerTotal.frequency)}
                </OHLCTableFootData>
              </tr>
            </OHLCTableFoot>
          </If>

          <If condition={isEmpty && showBestBid}>
            <OHLCTableFoot>
              <tr>
                <OHLCTableFootData>-</OHLCTableFootData>
                <OHLCTableFootData>-</OHLCTableFootData>
                <OHLCTableFootData colSpan={2}>TOTAL</OHLCTableFootData>
                <OHLCTableFootData>-</OHLCTableFootData>
                <OHLCTableFootData>-</OHLCTableFootData>
              </tr>
            </OHLCTableFoot>
          </If>
        </OHLCTable>
      </OHLCContainer>
    </SkeletonWrapper>
  );
};

export default TableOHLC;
